import React, { useMemo, useEffect, useRef, useState } from 'react'
import { Col, Form as FormAntd, Modal, Row } from 'antd'
import { Formik } from 'formik'
import { SelectValue } from 'antd/lib/select'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import moment from 'moment'

import { Request } from 'src/types'

import { ApplicationState } from 'src/store'
import { Button } from 'src/components/Ant'
import { DatePickerRange, Select } from 'src/components/Formik'
import { getCities } from 'src/core/store/ducks/cities/actions'
import {
  getVehicles,
  resetVehicles
} from 'src/core/store/ducks/vehicles/actions'
import { Option } from 'src/components/Formik/Select'
import { useStepsContext } from 'src/views/Requests/views/Form'

type SuggestionDate = {
  start_date?: number
  end_date?: number
}

type Props = {
  request: Request
}

const initialValues = {
  checkin_date: moment()
    .set({ hour: 8, minute: 0, second: 0, millisecond: 0 })
    .unix(),
  checkout_date: moment()
    .add(7, 'days')
    .set({ hour: 18, minute: 0, second: 0, millisecond: 0 })
    .unix(),
  trend_destination_id: '',
  trend_origin_id: ''
}

function Form({ request }: Props) {
  const dispatch = useDispatch()

  const refTimeout = useRef<NodeJS.Timeout>(null)

  const { cities, vehicles } = useSelector((state: ApplicationState) => ({
    cities: state.cities,
    vehicles: state.vehicles
  }))

  const [destinationCityList, setDestinationCityList] = useState<Option[]>([])
  const [originCityList, setOriginCityList] = useState<Option[]>([])
  const [targetInputCity, setTargetInputCity] = useState<
    'trend_destination_id' | 'trend_origin_id'
  >()

  const stepsContext = useStepsContext()

  useEffect(() => {
    const setFun =
      targetInputCity === 'trend_destination_id'
        ? setDestinationCityList
        : setOriginCityList

    setFun(
      cities.list.map<Option>(cityItem => ({
        label: `${cityItem.city_name} - ${cityItem.state_name}`,
        value: `${cityItem.trend_city_id}`
      }))
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cities.list])

  const suggestionDate = useMemo<SuggestionDate>(() => {
    const nextMonday = moment()
      .add(moment().isoWeekday() > 4 ? 2 : 1, 'weeks')
      .set({
        hour: 8 + Math.round(moment().utcOffset() / 60),
        minute: 0,
        second: 0,
        millisecond: 0
      })
      .isoWeekday(1)
    const momentRequestOn = moment.unix(request.requested_on).set({
      hour: 8 + Math.round(moment().utcOffset() / 60),
      minute: 0,
      second: 0,
      millisecond: 0
    })
    const start_date =
      nextMonday > momentRequestOn ? nextMonday : momentRequestOn
    const end_date = start_date.clone().add(1, 'd')
    return {
      start_date: start_date.unix(),
      end_date: end_date.unix()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.requested_on])
  const { t } = useTranslation()

  return (
    <Formik
      initialValues={{
        ...initialValues,
        checkout_date: suggestionDate?.end_date,
        checkin_date: suggestionDate?.start_date
      }}
      onSubmit={values => {
        const callSubmit = () => {
          dispatch(resetVehicles())
          stepsContext.setRequest({
            ...request,
            full_request_vehicle: []
          })

          let customer_supplier_id

          if (request.full_request_air && request.full_request_air.length) {
            customer_supplier_id =
              request.full_request_air[0].customer_supplier_id
          } else if (
            request.full_request_hotel &&
            request.full_request_hotel.length
          ) {
            customer_supplier_id =
              request.full_request_hotel[0].customer_supplier_id
          } else {
            const serviceTicket = request.request_tickets.find(ticketItem =>
              ['AIR', 'HOTEL', 'VEHICLE'].includes(
                ticketItem.request_ticket_type
              )
            )

            if (serviceTicket) {
              if (serviceTicket.request_ticket_air_selected) {
                customer_supplier_id =
                  serviceTicket.request_ticket_air_selected
                    .request_quote_air_trip.request_quote_air_one_way
                    .request_quote.customer_supplier_id
              } else if (serviceTicket.request_ticket_hotel_selected) {
                customer_supplier_id =
                  serviceTicket.request_ticket_hotel_selected
                    .request_quote_hotel_room.request_quote_hotel.request_quote
                    .customer_supplier_id
              }
            }
          }
          stepsContext.setBudgetAllocations({
            standard_type: 'VEHICLE',
            tickets: []
          })
          dispatch(
            getVehicles({
              customer_supplier_id,
              filters: {
                checkin_date: moment.unix(values.checkin_date).unix(),
                checkout_date: moment.unix(values.checkout_date).unix(),
                num_adults: request.request_user_data.length,
                trend_destination_id: values.trend_destination_id,
                trend_origin_id: values.trend_origin_id,
                vehicles: 1
              }
            })
          )
        }

        if (
          request.full_request_vehicle &&
          request.full_request_vehicle.length
        ) {
          Modal.confirm({
            cancelText: t('common.actions.cancel'),
            content: t('web.services.vehicle.alert-clear-search'),
            onOk: () => callSubmit()
          })
          return
        }

        callSubmit()
      }}
      validationSchema={Yup.object().shape({
        checkin_date: Yup.number().required('common.required'),
        checkout_date: Yup.number().required('common.required'),
        trend_destination_id: Yup.string().trim().required('common.required'),
        trend_origin_id: Yup.string().trim().required('common.required')
      })}
      render={formikProps => (
        <FormAntd onSubmit={formikProps.handleSubmit}>
          <Row gutter={24}>
            <Col span={6}>
              <Select
                dataTestId="vehicle-search-withdrawal"
                filterOption={false}
                formikProps={formikProps}
                label="web.services.vehicle.form.withdrawal.title"
                loading={
                  cities.loading && targetInputCity === 'trend_origin_id'
                }
                name="trend_origin_id"
                onChange={(value: SelectValue) =>
                  formikProps.setFieldValue('trend_origin_id', value)
                }
                onSearch={value => {
                  clearTimeout(refTimeout.current)
                  refTimeout.current = setTimeout(() => {
                    if (value) {
                      setTargetInputCity('trend_origin_id')
                      clearTimeout(refTimeout.current)
                      refTimeout.current = setTimeout(() => {
                        if (value) {
                          dispatch(getCities({ name: value }))
                        }
                      }, 500)
                    }
                  }, 500)
                }}
                options={originCityList}
                placeholder="web.services.vehicle.form.withdrawal.placeholder"
                showSearch
              />
            </Col>
            <Col span={6}>
              <Select
                dataTestId="vehicle-search-devolution"
                filterOption={false}
                formikProps={formikProps}
                label="web.services.vehicle.form.devolution.title"
                loading={
                  cities.loading && targetInputCity === 'trend_destination_id'
                }
                name="trend_destination_id"
                onChange={(value: SelectValue) =>
                  formikProps.setFieldValue('trend_destination_id', value)
                }
                onSearch={value => {
                  clearTimeout(refTimeout.current)
                  refTimeout.current = setTimeout(() => {
                    if (value) {
                      setTargetInputCity('trend_destination_id')
                      clearTimeout(refTimeout.current)
                      refTimeout.current = setTimeout(() => {
                        if (value) {
                          dispatch(getCities({ name: value }))
                        }
                      }, 500)
                    }
                  }, 500)
                }}
                options={destinationCityList}
                placeholder="web.services.vehicle.form.devolution.placeholder"
                showSearch
              />
            </Col>
            <Col span={12}>
              <DatePickerRange
                formikProps={formikProps}
                label="web.services.vehicle.form.period.placeholder"
                name={['checkin_date', 'checkout_date']}
                placeholder={[
                  'web.services.vehicle.form.withdrawal.title',
                  'web.services.vehicle.form.devolution.title'
                ]}
              />
            </Col>
          </Row>
          <FormAntd.Item>
            <Button
              block
              data-test-id="vehicle-search-action"
              htmlType="submit"
              icon="search"
              loading={vehicles.loading}
              title="common.actions.search"
              type="primary"
            />
          </FormAntd.Item>
        </FormAntd>
      )}
    />
  )
}

export default Form
